import { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import { Alert, Col, Row } from "react-bootstrap";
import TradingPlacesViewModel from "../../../viewModels/tradingPlaces/tradingPlacesViewModel";
import TradingPlaceType from "entities/tradingPlaceType";
import OpeningHoursKind from "entities/openingHoursKind";
import OpeningHoursIntervalResponse from "entities/openingHoursIntervalResponse";
import OpeningHoursWeekResponse from "entities/openingHoursWeekResponse";

const TradingPlaceSalesTimeView: ViewComponent<TradingPlacesViewModel> = observer(({ vm }) => {
  const standardOpeningHours = vm.place?.salespoint?.openingHours.find((hours) => hours.kind === OpeningHoursKind.Standard)?.week;
  const holidayOpeningHours = vm.place?.salespoint?.openingHours.filter((hours) => hours.kind === OpeningHoursKind.Holiday);
  const dayRows = [];
  const timeRows = [];

  const formatOpeningHours = (openingHoursDay?: OpeningHoursIntervalResponse[]) =>
    openingHoursDay ? (
      <>
        {openingHoursDay.length > 0 && <span className="d-inline-block">{vm.openingTimeRange(openingHoursDay[0])}</span>}
        {openingHoursDay.length > 1 && <span className="d-inline-block ml-2">{vm.openingTimeRange(openingHoursDay[1])}</span>}
      </>
    ) : (
      <span>{vm.translate("closed")}</span>
    );

  for (const dayOfWeek in new OpeningHoursWeekResponse()) {
    const dayKey = dayOfWeek as keyof OpeningHoursWeekResponse;
    const openingHoursDay = standardOpeningHours && dayKey in standardOpeningHours ? standardOpeningHours[dayKey] : undefined;

    dayRows.push(
      <Row key={dayOfWeek} className="mb-2">
        <Col xs="auto" style={{ minWidth: "5.4rem" }}>
          <span>{vm.translateGeneral(`week.${dayOfWeek}`)}</span>
        </Col>
        <Col className="d-block d-sm-none">{formatOpeningHours(openingHoursDay)}</Col>
      </Row>
    );

    timeRows.push(
      <Row key={dayOfWeek} className="mb-2">
        <Col xs="auto">{formatOpeningHours(openingHoursDay)}</Col>
      </Row>
    );
  }

  const salespointExternalDetailLink =
    vm.localizationService.locale === "cs"
      ? vm.place?.salespoint?.externalDetailLinkCs
      : vm.place?.salespoint?.externalDetailLinkEn;

  return (
    <>
      {vm.place && (vm.place.type === TradingPlaceType.Kiosk || (vm.place.salespoint && dayRows.length !== 0)) && (
        <div className="mb-3">
          <Row className="mb-2">
            <Col>
              <label>
                <strong>{vm.translateGeneral("salesTime")}</strong>
              </label>
            </Col>
          </Row>
          {salespointExternalDetailLink && (
            <Row className="mb-2">
              <Col>
                <label>
                  <strong>
                    <span className="text-danger">{vm.translateGeneral("outageInfoLabel")}</span>&nbsp;
                    <a href={salespointExternalDetailLink} target="_blank" rel="noreferrer">
                      {vm.translateGeneral("outageInfoLink")}
                    </a>
                  </strong>
                </label>
              </Col>
            </Row>
          )}
          {vm.closures.length > 0 && (
            <>
              {vm.closures.map((closure, index) => (
                <Row key={index}>
                  <Col>
                    <Alert variant="danger-light" className="border-none text-center">
                      {vm.closureInfo(closure)}
                    </Alert>
                  </Col>
                </Row>
              ))}
            </>
          )}
          {vm.place.type === TradingPlaceType.Kiosk ? (
            <Row>
              <Col>
                <label>{vm.translateGeneral("nonstop")}</label>
              </Col>
            </Row>
          ) : (
            <Row className="flex-nowrap">
              <Col className="flex-grow-1 flex-sm-grow-0">{dayRows}</Col>
              <Col className="d-none d-sm-block">{timeRows}</Col>
            </Row>
          )}
          {holidayOpeningHours && holidayOpeningHours.length > 0 && (
            <>
              <Row className="my-2">
                <Col>
                  <label>
                    <strong>{vm.translateGeneral("holidayTime")}</strong>
                  </label>
                </Col>
              </Row>
              <Row className="flex-nowrap">
                <Col className="flex-grow-1 flex-sm-grow-0">
                  {holidayOpeningHours
                    .sort((a, b) => new Date(a.validSince).getTime() - new Date(b.validSince).getTime())
                    .map((holidayOpeningHour) => {
                      const date = new Date(holidayOpeningHour.validSince);
                      const day = vm.holidayDay(holidayOpeningHour);
                      const openingHoursDay = day ? holidayOpeningHour.week[day] : undefined;
                      return (
                        <Row key={date.toISOString()} className="mb-2">
                          <Col xs="auto" style={{ minWidth: "5.4rem" }}>
                            <span>{vm.localizationService.formatDate(date)}</span>
                          </Col>
                          <Col xs="12" className="d-block d-sm-none">
                            {formatOpeningHours(openingHoursDay)}
                          </Col>
                        </Row>
                      );
                    })}
                </Col>
                <Col className="d-none d-sm-block">
                  {holidayOpeningHours.map((holidayOpeningHour) => {
                    const date = new Date(holidayOpeningHour.validSince);
                    const day = vm.holidayDay(holidayOpeningHour);
                    const openingHoursDay = day ? holidayOpeningHour.week[day] : undefined;
                    return (
                      <Row key={date.toISOString()} className="mb-2">
                        <Col xs="auto">{formatOpeningHours(openingHoursDay)}</Col>
                      </Row>
                    );
                  })}
                </Col>
              </Row>
            </>
          )}
        </div>
      )}
    </>
  );
});

export default TradingPlaceSalesTimeView;
